import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CircularLoading } from 'junto-ui';
import { Snackbar } from '@plataforma/components';
import { trimString } from '_/utils';
import { NewDownload } from '@plataforma/components/src/components/Icons';
import { PROCESS_STATUS, ENDORSEMENT_STATUS_CANCELED } from '@plataforma/utils';
import * as S from './CardStyles';
import MenuMore from './MenuMore';

function Card({
  policyStatusLabel,
  policyStatusId,
  menuItems,
  labelButton,
  policyHolder,
  labelStatusNumber,
  processNumber,
  controlId,
  processId,
  modality,
  showRetakeButton,
  endorsement,
  onClickCard,
  onClickActionButton,
  onOpenDuplicate,
  processNumberLabel,
  onClickDownloadPolicy,
  endorsementTypeId
}) {
  const { t } = useTranslation();
  const [state, setState] = useState({
    isLoadingDraft: false,
    isCopied: false
  });

  const showButtonDownloadPolicy = [
    PROCESS_STATUS.policyCompleted.groupName,
    PROCESS_STATUS.policyDuePayment.groupName
  ].includes(policyStatusId);

  const verifyIsCancellationEndorsement =
    ENDORSEMENT_STATUS_CANCELED === endorsementTypeId;

  const handleDraftLoader = async action => {
    setState({ ...state, isLoadingDraft: true });

    await action();
    setState({ ...state, isLoadingDraft: false });
  };

  const handleDraftSnackbar = async action => {
    setState({ ...state, isLoadingDraft: true });

    await action();
    setState({ ...state, isLoadingDraft: false, isCopied: true });
  };

  const newMenuItems =
    (menuItems &&
      menuItems.map(nav => {
        if (nav.id === 'draft-download')
          return { ...nav, action: () => handleDraftLoader(nav.action) };

        if (nav.id === 'draft-link')
          return { ...nav, action: () => handleDraftSnackbar(nav.action) };

        if (nav.id === 'duplicate')
          return { ...nav, action: () => onOpenDuplicate() };

        return nav;
      })) ||
    null;

  return (
    <S.Card onClick={onClickCard} data-testid="card">
      <S.InfoBlock>
        <S.Header>
          <S.Status
            status={
              verifyIsCancellationEndorsement
                ? PROCESS_STATUS.policyCanceled.groupName
                : policyStatusId
            }
          >
            {verifyIsCancellationEndorsement
              ? PROCESS_STATUS.policyCanceled.title
              : policyStatusLabel}
          </S.Status>

          <S.Title>{trimString(policyHolder, 40)}</S.Title>

          {menuItems && !labelButton && (
            <S.Menu>
              <Snackbar
                open={state.isCopied}
                message={t('generic.successSnackbarMessage')}
                variant="success"
                onClose={() => setState({ ...state, isCopied: false })}
              />

              {(state.isLoadingDraft && <CircularLoading size="24" />) || (
                <MenuMore
                  items={
                    verifyIsCancellationEndorsement
                      ? [newMenuItems[1]]
                      : newMenuItems
                  }
                />
              )}
            </S.Menu>
          )}
        </S.Header>

        <S.Body>
          <S.Content>
            {!!processId && (
              <S.Item>
                <S.Label>{labelStatusNumber}</S.Label>
                <S.Value>{processId}</S.Value>
              </S.Item>
            )}

            <S.Item>
              <S.Label>
                {processNumber
                  ? t(`card.processNumber.${processNumberLabel}`)
                  : t('card.modality')}
              </S.Label>
              <S.Value>
                {processNumber ? trimString(processNumber, 50) : modality}
              </S.Value>
            </S.Item>

            {showButtonDownloadPolicy && (
              <S.DownloadButton
                onClick={e => {
                  e.stopPropagation();
                  onClickDownloadPolicy(processNumber);
                }}
              >
                <NewDownload />
                <S.labelButtonDownload>Baixar apólice</S.labelButtonDownload>
              </S.DownloadButton>
            )}
            {!endorsement && (
              <S.Item>
                <S.Label>{t('card.modality')}</S.Label>
                <S.Value>{modality}</S.Value>
              </S.Item>
            )}
            {controlId && (
              <S.Item>
                <S.Label>{t('card.controlId')}</S.Label>
                <S.Value>{controlId}</S.Value>
              </S.Item>
            )}
          </S.Content>
        </S.Body>
        {endorsement && <S.Endorsement>{endorsement}</S.Endorsement>}
      </S.InfoBlock>

      {labelButton && showRetakeButton && (
        <S.ActionBlock>
          <S.ButtonContainer>
            <S.Button
              className="js-resume-process"
              onClick={e => {
                e.stopPropagation();
                onClickActionButton(e);
              }}
              variant="primary"
            >
              {labelButton}
            </S.Button>
          </S.ButtonContainer>
        </S.ActionBlock>
      )}
    </S.Card>
  );
}

Card.propTypes = {
  endorsement: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool
  ]),
  processId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  labelButton: PropTypes.string,
  policyStatusId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  policyStatusLabel: PropTypes.string,
  policyHolder: PropTypes.string.isRequired,
  menuItems: PropTypes.arrayOf(PropTypes.object),
  labelStatusNumber: PropTypes.string,
  processNumber: PropTypes.string,
  processNumberLabel: PropTypes.string,
  controlId: PropTypes.string,
  modality: PropTypes.string.isRequired,
  onClickCard: PropTypes.func.isRequired,
  onClickActionButton: PropTypes.func,
  onOpenDuplicate: PropTypes.func,
  onClickDownloadPolicy: PropTypes.func,
  showRetakeButton: PropTypes.bool,
  endorsementTypeId: PropTypes.number
};

export default memo(Card);
